import React from 'react';
import { WeezHeader, Message, Button, Icon, Table, SelectHierarchy, Input, ToastContext, OptionalSection, Tooltip, Loader } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import LoadingCard from '../../../components/loadingCard';
import { useForm } from '../../../utils/validators';
import ConfirmModal from './components/confirmModal';
import Styles from './mapping.module.css';
import Subscription from '../../../models/subscription';
import SubscriptionLinkModel from '../../../models/subscriptionLink';

const TableCustomItem = ({ form, item, index, options, handleChange }) => {
    const { t } = useTranslation();

    const filteredOptions = React.useMemo(() => {
        if (options.length === 0) {
            return [];
        }

        const keys_default = Object.values(form.field_mapping).map(subList => subList[0]);
        const unconventionalFieldRegex = /[^a-zA-Z0-9_-]+/; // Identifies field names that should not be displayed raw to the user

        return options
            .filter(key => {
                if (key === item) {
                    return true;
                }
                return !keys_default.includes(key) && !form.custom_field_mapping.includes(key);
            })
            .map(i => {
                return {
                    key: i,
                    value: i,
                    text: unconventionalFieldRegex.test(i) ? (
                        <Tooltip className={Styles['mapping-tooltip']} title={i} triggerOnHover={true}>
                            {' '}
                            {t('ticket.webservice.mapping.custom_field')}{' '}
                        </Tooltip>
                    ) : (
                        i
                    )
                };
            });
    }, [options, form]);

    return (
        <Table.Tr className={Styles['mapping-row']}>
            <Table.Td className={Styles['mapping-table-large']}>
                <SelectHierarchy
                    key={`${index}_${item}`}
                    height="mini"
                    disabled={!options.length}
                    options={filteredOptions}
                    value={item}
                    placeholder={t('ticket.attendees.imports.steps.data.table.select')}
                    onChange={value => handleChange(value, index)}
                    onClear={() => handleChange(null, index)}
                />
            </Table.Td>
            <Table.Td className={Styles['mapping-table-small']}>
                <Icon name="arrow-right" circular />
            </Table.Td>
            <Table.Td className={Styles['mapping-table-large']}>
                <Input disabled value={t(`ticket.webservice.mapping.fields.form_answer`)} />
            </Table.Td>
        </Table.Tr>
    );
};

const TableItem = ({ form, item, index, options, handleChange }) => {
    const { t } = useTranslation();

    const filteredOptions = React.useMemo(() => {
        if (options.length === 0) {
            return [];
        }

        const unconventionalFieldRegex = /[^a-zA-Z0-9_-]+/; // Identifies field names that should not be displayed raw to the user
        return options.map(i => {
            return {
                key: i,
                value: i,
                text: unconventionalFieldRegex.test(i) ? (
                    <Tooltip className={Styles['mapping-tooltip']} title={i} triggerOnHover={true}>
                        {' '}
                        {t('ticket.webservice.mapping.custom_field')}{' '}
                    </Tooltip>
                ) : (
                    i
                )
            };
        });
    }, [options, form]);

    return (
        <Table.Tr className={Styles['mapping-row']}>
            <Table.Td className={Styles['mapping-table-large']}>
                <SelectHierarchy
                    key={`${index}`}
                    height="mini"
                    disabled={!options.length}
                    options={filteredOptions}
                    value={item[1]}
                    placeholder={t('ticket.attendees.imports.steps.data.table.select')}
                    onChange={value => handleChange(value, item, index)}
                    onClear={() => handleChange(null, item, index)}
                />
            </Table.Td>
            <Table.Td className={Styles['mapping-table-small']}>
                <Icon name="arrow-right" circular />
            </Table.Td>
            <Table.Td className={Styles['mapping-table-large']}>
                <Input disabled value={t(`ticket.webservice.mapping.fields.${item[0]}`)} />
            </Table.Td>
        </Table.Tr>
    );
};

// formatDefaultfield initiate content of form for weezevent field, so we only wan't those in data
// Using final_field_mapping because final_field_mapping either have a default value if not mapped
// or the value mapped by the user
// If the value in final_field_mapping is available in the user's options then we put it by default
function formatDefaultFields(data, final_field_mapping, options) {
    let _data = [];

    if (options) {
        data.forEach(k => {
            let val_map = null;
            if (final_field_mapping[k] === 'barcode || barcode_id || ticket_identifier') {
                const barcode_keys = ['barcode', 'barcode_id', 'ticket_identifier'];
                val_map = barcode_keys.find(key => options.includes(key)) || null;
                // When "first_name || firstname" and "last_name || lastname" is changed to "first_name" and "last_name" delete this lines
            } else if (final_field_mapping[k] === 'first_name || firstname' || final_field_mapping[k] === 'last_name || lastname') {
                const keys = k === 'first_name' ? ['first_name', 'firstname'] : ['last_name', 'lastname'];
                val_map = keys.find(key => options.includes(key)) || null;
                // END OF DELETE
            } else {
                val_map = options.includes(final_field_mapping[k]) ? final_field_mapping[k] : null;
            }
            _data.push([k, val_map]);
        });
    }
    return _data;
}

export default function Mapping({ organizationId, subscriptionId }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [submitting, setSubmitting] = React.useState(false);
    const [confirmModal, setConfirmModal] = React.useState(false);
    const data = ['barcode', 'price_id', 'price_name', 'paid_price', 'first_name', 'last_name']; //Names are corresponding to the right keys in field_mapping
    const [ticketFilters, setTicketFilters] = React.useState([]);
    const [options, setOptions] = React.useState([]);

    const [form, setForm, setFormObject] = useForm({
        field_mapping: formatDefaultFields(data),
        custom_field_mapping: ['']
    });

    const [, loadingTicketsFilters] = Subscription.useApiModel(
        {
            organizationId: organizationId,
            id: subscriptionId,
            get_filters: true,
            onSuccess: response => {
                const { __meta, __params, ...filteredObject } = response.object;
                setTicketFilters(Object.keys(filteredObject));
            }
        },
        [organizationId, subscriptionId]
    );

    const [, loadingSubscription, syncSubscription] = Subscription.useApiModel(
        {
            organizationId: organizationId,
            id: subscriptionId,
            launch: Boolean(!loadingTicketsFilters),
            onSuccess: sub => {
                const fields = Object.entries(sub.object.field_mapping);
                const customFields = Object.fromEntries(fields.filter(([key, value]) => !data.includes(key)));
                const _options = [...new Set([...Object.values(sub.object.field_mapping), ...ticketFilters])]; // Some available fields can be add directly in field_mapping
                setOptions(_options);
                setFormObject({
                    ...sub.object,
                    field_mapping: formatDefaultFields(data, sub.object.final_field_mapping, _options),
                    custom_field_mapping: Object.keys(customFields).length ? [...Object.keys(customFields), ''] : ['']
                });
            }
        },
        [organizationId, subscriptionId, setFormObject, loadingTicketsFilters]
    );

    const [subscriptionLinks, loadSubscriptionsLink] = SubscriptionLinkModel.useApiModel(
        {
            allow_cache: false,
            cache: false,
            organizationId: organizationId,
            subscriptionId: subscriptionId
        },
        [organizationId, subscriptionId]
    );

    const onSubmit = React.useCallback(() => {
        setSubmitting(true);

        let field_mapping_format = {};
        Object.entries(form.field_mapping).forEach(([key, value]) => {
            if (value[1]) {
                field_mapping_format[value[0]] = value[1];
            }
        });
        Object.entries(form.custom_field_mapping).forEach(([key, value]) => {
            if (value) {
                field_mapping_format[value] = value;
            }
        });

        let subscription = new Subscription(form);
        delete subscription.custom_field_mapping;
        subscription.field_mapping = field_mapping_format;

        subscription
            .save({ organizationId })
            .then(() => {
                toast.success(t('ticket.webservice.mapping.success'));
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.webservice.mapping.error'));
            })
            .finally(() => {
                setSubmitting(false);
                setConfirmModal(false);
                syncSubscription();
            });
    }, [form, organizationId, toast, syncSubscription]);

    const handleChange = React.useCallback(
        (value, item, index) => {
            let datas = [...form.field_mapping];
            datas[index] = [item[0], value];
            setForm('field_mapping', datas);
        },
        [form, setForm]
    );

    const handleChangeAdditionalData = React.useCallback(
        (value, index) => {
            let datas = [...form.custom_field_mapping];
            if (value) {
                datas[index] = value;
            } else {
                datas.splice(index, 1);
            }

            if (datas[datas.length - 1] !== '') {
                datas.push('');
            }

            setForm('custom_field_mapping', datas);
        },
        [form, setForm]
    );

    const isDisabled = React.useMemo(() => {
        if (!options) {
            return true;
        }
        return !Object.keys(options).length;
    }, [options]);

    if (loadingSubscription || loadingTicketsFilters || loadSubscriptionsLink) {
        return <Loader />;
    }

    return (
        <>
            <ConfirmModal show={confirmModal} onSubmit={onSubmit} onClose={() => setConfirmModal(false)} isSubmitting={submitting} subscriptionLinks={subscriptionLinks} />
            <WeezHeader
                title={t('ticket.webservice.mapping.title')}
                rightComponent={[
                    <Button primary key="primary" isLoading={submitting} disabled={isDisabled} label={t('ticket.common.action.save')} onClick={() => setConfirmModal(true)} />
                ]}
            />
            {options.length < 0 ? (
                <Message type="danger" title={t('ticket.webservice.mapping.message-danger.title')}>
                    {t('ticket.webservice.mapping.message-danger.info')}
                </Message>
            ) : (
                <Message type="default" title={t('ticket.webservice.mapping.message-default.title')}>
                    {t('ticket.webservice.mapping.message-default.info')}
                </Message>
            )}
            <div className={Styles['step-body']}>
                <LoadingCard loading={false}>
                    <Table useNew className={Styles['mapping-table']}>
                        <Table.THead>
                            <Table.Tr>
                                <th className={Styles['mapping-table-large']}>{t('ticket.webservice.mapping.table-provider-field')}</th>
                                <th /> {/* Empty header for icon display */}
                                <th className={Styles['mapping-table-large']}>{t('ticket.webservice.mapping.table-weez-field')}</th>
                            </Table.Tr>
                        </Table.THead>
                        {form.field_mapping.map((item, index) => {
                            return (
                                <>
                                    <TableItem
                                        key={`${index}_default_field`}
                                        index={index}
                                        item={item}
                                        options={options}
                                        form={form}
                                        setForm={setForm}
                                        handleChange={handleChange}
                                    />
                                </>
                            );
                        })}
                    </Table>
                    <OptionalSection
                        className={`${Styles['form_code_modal']} ${Styles['override-height-max']}`}
                        label={t('ticket.webservice.mapping.additional-field')}
                        open={true}
                    >
                        <Table useNew className={Styles['mapping-table']}>
                            {form.custom_field_mapping.map((item, index) => {
                                return (
                                    <>
                                        <TableCustomItem
                                            key={`${index}_custom_field`}
                                            index={index}
                                            item={item}
                                            options={options}
                                            form={form}
                                            setForm={setForm}
                                            handleChange={handleChangeAdditionalData}
                                        />
                                    </>
                                );
                            })}
                        </Table>
                    </OptionalSection>
                </LoadingCard>
            </div>
        </>
    );
}
